// jsaForm/core/basicInfo.actions.js

import { v4 as uuidv4 } from "uuid";
import {
  getSiteDetails,
  findNearestHospital,
  loadAddressSuggestions,
  reverseGeocode,
} from "../../../../api/AddressApi";
import { getCurrentPositionOnce } from "../../../../utils/geolocation";

export const makeJsaCoreBasicInfoActions = ({ setJsaUi, bump, emitJsaPatch }) => ({
  updateJsaClientName(jsaData, clientName, emitPatch) {
    jsaData.jsaBasicInfo.setJsaClientName(clientName);
    // Only emit patch on blur (when emitPatch is provided)
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        jsaClientName: clientName,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaJobDescription(jsaData, jobDescription, emitPatch) {
    jsaData.jsaBasicInfo.setJsaJobDescription(jobDescription);
    // Only emit patch on blur (when emitPatch is provided)
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        jsaJobDescription: jobDescription,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  selectJsaSupervisor(jsaData, supervisor, emitPatch) {
    jsaData.jsaBasicInfo.setSupervisor(supervisor);
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        supervisorId: supervisor,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaCompletedTime(jsaData, completedTime, emitPatch) {
    jsaData.jsaBasicInfo.setJsaCompletedTime(completedTime);
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        jsaCompletedTime: completedTime,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaCompletedDate(jsaData, completedDate, emitPatch) {
    jsaData.jsaBasicInfo.setJsaCompletedDate(completedDate);
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        jsaCompletedDate: jsaData.jsaBasicInfo.jsaCompletedDate, // Use formatted value from model
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateGoogleSessionToken() {
    const newToken = uuidv4();
    setJsaUi({
      core: (prev) => ({
        ...prev,
        basicInfo: { ...(prev?.basicInfo || {}), googleSessionToken: newToken },
      }),
    });
    bump();
  },

  // Stash the ranked facility candidate list in transient UI state (drives the
  // medical facility dropdown). Not persisted or broadcast.
  setMedicalFacilityOptions(options) {
    setJsaUi({
      core: (prev) => ({
        ...prev,
        basicInfo: {
          ...(prev?.basicInfo || {}),
          medicalFacilityOptions: options || [],
        },
      }),
    });
  },

  async getMedicalFacility(jsaData, lat, lng, emitPatch) {
    const options = await findNearestHospital(lat, lng);
    this.setMedicalFacilityOptions(options);
    // Auto-fill the top choice (nearest hospital) - unchanged save behavior.
    const top = options[0] || null;
    jsaData.jsaBasicInfo.setMedicalFacility(top?.displayName || null);
    jsaData.jsaBasicInfo.setMedicalFacilityFormattedAddress(
      top?.formattedAddress || null
    );
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        medicalFacility: top?.displayName || null,
        medicalFacilityAddress: top?.formattedAddress || null,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  // Apply a facility chosen from the dropdown: sets both name + address in one patch.
  selectMedicalFacility(jsaData, option, emitPatch) {
    jsaData.jsaBasicInfo.setMedicalFacility(option?.displayName || null);
    jsaData.jsaBasicInfo.setMedicalFacilityFormattedAddress(
      option?.formattedAddress || null
    );
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        medicalFacility: option?.displayName || null,
        medicalFacilityAddress: option?.formattedAddress || null,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  async updateSiteLocation(jsaData, selected, googleSessionToken, emitPatch) {
    if (!selected) {
      jsaData.jsaBasicInfo.setSiteLocation(null);
      // Clear stale facility candidates when the site is cleared.
      this.setMedicalFacilityOptions([]);
      if (emitPatch) {
        const patch = jsaData.jsaBasicInfo.toPatch({
          siteLocation: null,
        });
        emitJsaPatch(patch, emitPatch);
      }
      bump();
      return;
    }
    const details = await getSiteDetails(selected.value, googleSessionToken);
    if (!details) {
      jsaData.jsaBasicInfo.setSiteLocation(selected);
      if (emitPatch) {
        const patch = jsaData.jsaBasicInfo.toPatch({
          siteLocation: selected,
        });
        emitJsaPatch(patch, emitPatch);
      }
      bump();
      this.updateGoogleSessionToken();
      return;
    }
    const siteLocation = {
      label: details?.displayName?.text
        ? details.displayName.text + ", " + details.formattedAddress
        : details.formattedAddress,
      value: selected.value,
      lat: details?.location?.latitude,
      lng: details?.location?.longitude,
    };
    jsaData.jsaBasicInfo.setSiteLocation(siteLocation);
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        siteLocation: siteLocation,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
    await this.getMedicalFacility(jsaData, siteLocation.lat, siteLocation.lng, emitPatch);
    this.updateGoogleSessionToken();
  },

  // Set the site location from raw coordinates (shared by current-location and
  // manual-coordinate entry). Reverse-geocodes for a display label, falling back
  // to the raw coords when offline, then triggers the medical-facility lookup.
  async _applyCoordsToSite(jsaData, lat, lng, emitPatch) {
    let label = null;
    try {
      label = await reverseGeocode(lat, lng);
    } catch {
      label = null;
    }
    if (!label) {
      label = `${lat}, ${lng}`;
    }
    const siteLocation = { label, value: "", lat, lng };
    jsaData.jsaBasicInfo.setSiteLocation(siteLocation);
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({ siteLocation });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
    await this.getMedicalFacility(jsaData, lat, lng, emitPatch);
    return { ok: true };
  },

  // Use the device's current GPS position as the site location.
  // Returns { ok, error } so the component can surface permission/timeout errors.
  async useCurrentLocationForSite(jsaData, emitPatch) {
    let coords;
    try {
      coords = await getCurrentPositionOnce();
    } catch (err) {
      return {
        ok: false,
        error:
          err?.message ||
          "Unable to get your current location. Check location permissions.",
      };
    }
    return this._applyCoordsToSite(
      jsaData,
      coords.latitude,
      coords.longitude,
      emitPatch
    );
  },

  // Set the site location from manually-entered coordinates.
  // Returns { ok, error } so the component can surface validation errors.
  async applyManualCoordinates(jsaData, latStr, lngStr, emitPatch) {
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return {
        ok: false,
        error:
          "Enter a valid latitude (-90 to 90) and longitude (-180 to 180).",
      };
    }
    return this._applyCoordsToSite(jsaData, lat, lng, emitPatch);
  },

  updateAddressValidation(bool) {
    setJsaUi({
      core: (prev) => ({
        ...prev,
        basicInfo: { ...(prev?.basicInfo || {}), useAddressValidation: bool },
      }),
    });
    bump();
  },

  updateManualSiteLocation(jsaData, siteLocation, emitPatch) {
    jsaData.jsaBasicInfo.setSiteLocation(siteLocation);
    // Only emit patch on blur (when emitPatch is provided)
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        siteLocation: siteLocation,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateManualMedicalFacility(jsaData, medicalFacility, emitPatch) {
    jsaData.jsaBasicInfo.setMedicalFacility(medicalFacility);
    // Only emit patch on blur (when emitPatch is provided)
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        medicalFacility: medicalFacility,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateManualMedicalFacilityAddress(jsaData, address, emitPatch) {
    jsaData.jsaBasicInfo.setMedicalFacilityFormattedAddress(address);
    // Only emit patch on blur (when emitPatch is provided)
    if (emitPatch) {
      const patch = jsaData.jsaBasicInfo.toPatch({
        medicalFacilityAddress: address,
      });
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  async loadAddressSuggestions(inputValue, googleSessionToken) {
    return loadAddressSuggestions(inputValue, googleSessionToken);
  },
});
